// src/config/firebase.js
import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAnalytics, isSupported } from "firebase/analytics";
import { getAuth } from "firebase/auth";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  abandonedCartDocumentId,
  buildAbandonedCartWithdrawal,
} from "../utils/abandonedCart";

const firebaseConfig = {
  apiKey: "AIzaSyBgjSzVfu3KgdXLnBCMi1ude890VuJZS9w",
  authDomain: "romi-store-ec.firebaseapp.com",
  projectId: "romi-store-ec",
  storageBucket: "romi-store-ec.firebasestorage.app",
  messagingSenderId: "571919302863",
  appId: "1:571919302863:web:4f4dc40c0fc0f329d277ba",
  measurementId: "G-YCVPMSHRME",
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const appCheckSiteKey = String(
  process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY || "",
).trim();

if (
  typeof window !== "undefined"
  && process.env.NODE_ENV !== "production"
  && process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN
) {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN =
    process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN === "true"
      ? true
      : process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
}

if (typeof window !== "undefined" && appCheckSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const db = getFirestore(app);
export const storage = getStorage(app);
const functionsClient = getFunctions(app);
const createAuthoritativeOrderCall = httpsCallable(
  functionsClient,
  "createAuthoritativeOrder",
  { limitedUseAppCheckTokens: true },
);
export let analytics = null;

if (typeof window !== "undefined") {
  isSupported()
    .then((supported) => {
      if (supported) analytics = getAnalytics(app);
    })
    .catch(() => {});
}

// ─── PRODUCTOS ────────────────────────────────────────────────

export const CATEGORIAS = [
  { id: "todos", nombre: "Todos", emoji: "🛍️" },
  { id: "hogar-decoracion", nombre: "Hogar y Decoración", emoji: "🏠" },
  { id: "belleza-salud", nombre: "Belleza y Salud", emoji: "💄" },
  { id: "tecnologia", nombre: "Tecnología", emoji: "💻" },
  { id: "moda-ropa", nombre: "Moda y Ropa", emoji: "👗" },
  { id: "deportes", nombre: "Deportes", emoji: "🏋️" },
  { id: "juguetes-bebes", nombre: "Juguetes y Bebés", emoji: "🧸" },
  { id: "mascotas", nombre: "Mascotas", emoji: "🐾" },
  { id: "automotriz", nombre: "Automotriz", emoji: "🚗" },
  { id: "cocina", nombre: "Cocina", emoji: "🍳" },
  {
    id: "juegos-entretenimiento",
    nombre: "Juegos y Entretenimiento",
    emoji: "🎮",
  },
];

// Obtener todos los productos
export const getProductos = async (categoria = null) => {
  try {
    let q;
    if (categoria && categoria !== "todos") {
      q = query(
        collection(db, "productos"),
        where("categoria", "==", categoria),
        where("disponible", "==", true),
      );
    } else {
      q = query(collection(db, "productos"), where("disponible", "==", true));
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error("Error getProductos:", err);
    return [];
  }
};

// Obtener un producto por ID
export const getProducto = async (id) => {
  try {
    const snap = await getDocs(collection(db, "productos"));
    const doc_ = snap.docs.find((d) => d.id === id);
    return doc_ ? { id: doc_.id, ...doc_.data() } : null;
  } catch (err) {
    console.error("Error getProducto:", err);
    return null;
  }
};

// Crear producto (admin)
export const crearProducto = async (producto) => {
  const docRef = await addDoc(collection(db, "productos"), {
    ...producto,
    disponible: true,
    creadoEn: new Date().toISOString(),
  });
  return docRef.id;
};

// ─── PROMOCIONES ──────────────────────────────────────────────

export const getPromociones = async () => {
  try {
    const snap = await getDocs(collection(db, "promociones"));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error("Error getPromociones:", err);
    return [];
  }
};

export const crearPromocion = async (promocion) => {
  const docRef = await addDoc(collection(db, "promociones"), promocion);
  return docRef.id;
};

export const eliminarPromocion = async (id) => {
  await deleteDoc(doc(db, "promociones", id));
};

export const actualizarPromocion = async (id, datos) => {
  const docRef = doc(db, "promociones", id);
  await updateDoc(docRef, datos);
};

// Actualizar producto (admin)
export const actualizarProducto = async (id, datos) => {
  await updateDoc(doc(db, "productos", id), datos);
};

// Eliminar producto (admin)
export const eliminarProducto = async (id) => {
  await deleteDoc(doc(db, "productos", id));
};

// Subir imagen a Firebase Storage
export const subirImagen = async (file) => {
  const result = await subirImagenEn(file, "productos");
  return result.url;
};

// ─── OFERTAS VIRALES ──────────────────────────────────────────

export const getOfertasVirales = async ({ onlyActive = false } = {}) => {
  const snap = await getDocs(collection(db, "ofertasVirales"));
  return snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((item) => !onlyActive || item.estado === "publicado")
    .sort((a, b) => Number(a.orden || 0) - Number(b.orden || 0));
};

export const crearOfertaViral = async (oferta) => {
  const docRef = await addDoc(collection(db, "ofertasVirales"), {
    ...oferta,
    creadoEn: new Date().toISOString(),
    actualizadoEn: new Date().toISOString(),
  });
  return docRef.id;
};

export const actualizarOfertaViral = async (id, datos) => {
  await updateDoc(doc(db, "ofertasVirales", id), {
    ...datos,
    actualizadoEn: new Date().toISOString(),
  });
};

export const eliminarOfertaViral = async (id) => {
  await deleteDoc(doc(db, "ofertasVirales", id));
};

export const eliminarImagenPorPath = async (path) => {
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (error) {
    if (error.code !== "storage/object-not-found") throw error;
  }
};

export const subirImagenEn = async (file, folder = "productos") => {
  const safeName = String(file.name || "imagen").replace(/[^a-zA-Z0-9._-]/g, "-");
  const path = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: file.type || "image/webp" });
  return { url: await getDownloadURL(storageRef), path };
};

// Los precios y productos se resuelven dentro de Cloud Functions. El navegador
// solo envía identificadores y cantidades; nunca es la fuente autoritativa.
export const guardarOrden = async (orden) => {
  const requestId = String(
    orden?.requestId || orden?.orderId || `order-${Date.now()}`,
  );
  const result = await createAuthoritativeOrderCall({
    source: "catalog",
    requestId,
    cliente: orden?.cliente,
    items: Array.isArray(orden?.productos)
      ? orden.productos.map((item) => ({
          id: item.id,
          quantity: item.cantidad,
        }))
      : [],
    consentimientoMeta: orden?.consentimientoMeta === true,
    metaConsentVersion: orden?.metaConsentVersion,
    attribution: orden?.attribution,
  });
  return result.data.id;
};

export const crearPedidoLanding = async (pedido) => {
  const result = await createAuthoritativeOrderCall({
    source: "campaign",
    requestId: pedido.requestId,
    landingSlug: pedido.landingSlug,
    offerQuantity: pedido.offerQuantity,
    cliente: pedido.cliente,
    consentimientoMeta: pedido.consentimientoMeta === true,
    metaConsentVersion: pedido.metaConsentVersion,
    attribution: pedido.attribution,
  });
  return result.data;
};

// ─── CARRITOS ABANDONADOS DE LANDINGS ────────────────────────

const abandonedCartRef = (carrito) =>
  doc(db, "carritosAbandonados", abandonedCartDocumentId(carrito));

const abandonedCartFields = (carrito) => ({
  visitorId: carrito.visitorId,
  landingSlug: carrito.landingSlug,
  productoId: carrito.productoId,
  productoNombre: carrito.productoNombre,
  ofertaCantidad: carrito.ofertaCantidad,
  ofertaPrecio: carrito.ofertaPrecio,
  nombre: carrito.nombre,
  telefono: carrito.telefono,
  creadoEn: carrito.creadoEn,
});

export const guardarCarritoAbandonado = async (carrito) => {
  const reference = abandonedCartRef(carrito);
  const now = new Date().toISOString();

  // Escritura ciega: las reglas pueden permitir create/update sin exponer
  // públicamente los datos mediante una lectura previa.
  await setDoc(reference, {
    ...abandonedCartFields(carrito),
    estado: "pendiente",
    actualizadoEn: now,
  });

  return reference.id;
};

export const marcarCarritoConvertido = async (carrito) => {
  const reference = abandonedCartRef(carrito);
  const now = new Date().toISOString();

  await setDoc(reference, {
    ...abandonedCartFields(carrito),
    estado: "convertido",
    actualizadoEn: now,
  });

  return reference.id;
};

export const retirarCarritoAbandonado = async ({
  landingSlug,
  visitorId,
  productoId,
  creadoEn,
}) => {
  const reference = abandonedCartRef({ landingSlug, visitorId });
  await setDoc(reference, buildAbandonedCartWithdrawal({
    visitorId,
    landingSlug,
    productId: productoId,
    createdAt: creadoEn,
  }));
};

export const getProductosAdmin = async () => {
  try {
    const snap = await getDocs(collection(db, "productos"));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error("Error getProductosAdmin:", err);
    return [];
  }
};

export const getOrdenes = async () => {
  try {
    const snap = await getDocs(collection(db, "ordenes"));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(b.creadoEn || "").localeCompare(String(a.creadoEn || "")));
  } catch (err) {
    console.error("Error getOrdenes:", err);
    return [];
  }
};

export const actualizarOrden = async (id, datos) => {
  await updateDoc(doc(db, "ordenes", id), {
    ...datos,
    actualizadoEn: new Date().toISOString(),
  });
};

export const auth = getAuth(app);
export const functions = functionsClient;
